import { Theme } from '@logto/schemas';

import { themeStorageKey } from '@/utils/storage-keys';

import styles from './index.module.scss';

/**
 * Where a person's theme choice is remembered.
 *
 * ⚠️ Read by the blocking script in each app's `index.html` too. Change it in
 * one place and the page paints the wrong theme for a frame before React
 * corrects it — a flash, not an error.
 */
export { themeStorageKey } from '@/utils/storage-keys';

type Props = {
  readonly theme: Theme;
  readonly onChange: (theme: Theme) => void;
  readonly className?: string;
};

/**
 * Light/dark switch, shared by the sign-in screen and the Account Center.
 *
 * ⚠️ It writes THREE places, and all three are needed:
 *   - `localStorage`, so the choice survives a reload
 *   - `documentElement.dataset.theme`, so the page repaints immediately rather
 *     than waiting for React
 *   - the caller's state, so components reading `theme` re-render
 *
 * Dropping the second is the subtle one: state updates, tokens do not, and only
 * the parts that read `theme` in JS change colour.
 */
const ThemeToggle = ({ theme, onChange, className }: Props) => (
  <button
    aria-label="Toggle theme"
    className={className ?? styles.themeToggle}
    type="button"
    onClick={() => {
      const next = theme === Theme.Dark ? Theme.Light : Theme.Dark;

      localStorage.setItem(themeStorageKey, next);
      document.documentElement.dataset.theme = next;
      onChange(next);
    }}
  >
    {theme === Theme.Dark ? (
      <svg fill="none" height="18" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24" width="18">
        <circle cx="12" cy="12" r="4" />
        <path d="M12 2v2M12 20v2M2 12h2M20 12h2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M19.1 4.9l-1.4 1.4M6.3 17.7l-1.4 1.4" />
      </svg>
    ) : (
      <svg fill="none" height="18" stroke="currentColor" strokeWidth="1.6" viewBox="0 0 24 24" width="18">
        <path d="M21 12.8A9 9 0 1 1 11.2 3a7 7 0 0 0 9.8 9.8Z" />
      </svg>
    )}
  </button>
);

export default ThemeToggle;
